/**
 * `wayai record-types …` — the schema a record conforms to. Top-level: WayAI
 * owns no `record-types` concept.
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { historyQuery, outputFormat, parseData, pathSegment, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildRecordTypesCommand(): Command {
  const recordTypes = new Command('record-types').description('Manage record_types');

  recordTypes
    .command('list')
    .description('List all record_types in a base')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/record-types`), outputFormat(this));
    });

  recordTypes
    .command('get <id>')
    .description('Get a record_type')
    .action(async function (this: Command, id: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/record-types/${pathSegment(id, 'record_type')}`), outputFormat(this));
    });

  recordTypes
    .command('upsert <id>')
    .description('Create or update a record_type')
    .requiredOption('--name <name>', 'RecordType name')
    .option('--description <desc>', 'Description')
    .option('--schema <json>', 'JSON Schema (inline JSON or @filename)')
    .option('--icon <icon>', 'Icon name')
    .option('--color <color>', 'Hex color')
    .option('--sources <json>', 'External sources config (inline JSON or @filename)')
    .action(async function (
      this: Command,
      id: string,
      opts: {
        name: string;
        description?: string;
        schema?: string;
        icon?: string;
        color?: string;
        sources?: string;
      },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const body: Record<string, unknown> = { name: opts.name };
      if (opts.description) body.description = opts.description;
      if (opts.schema) body.json_schema = parseData(opts.schema);
      if (opts.icon) body.icon = opts.icon;
      if (opts.color) body.color = opts.color;
      if (opts.sources) body.sources = parseData(opts.sources);
      printOutput(
        await client.request('PUT', `/v1/${base}/record-types/${pathSegment(id, 'record_type')}`, body),
        outputFormat(this),
      );
    });

  recordTypes
    .command('delete <id>')
    .description('Delete a record_type')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      if (!opts.yes && !(await confirm(`Delete record_type "${id}"? This cannot be undone.`))) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/record-types/${pathSegment(id, 'record_type')}`);
      console.log('Deleted');
    });

  recordTypes
    .command('history <id>')
    .description(
      'View the change history (audit log) for a record_type — every schema/config version, who changed it, and when',
    )
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .option('--diff', 'Include field-level diffs between versions')
    .action(async function (
      this: Command,
      id: string,
      opts: { limit?: string; offset?: string; diff?: boolean },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/audit/record-types/${pathSegment(id, 'record_type')}${historyQuery(opts)}`),
        outputFormat(this),
      );
    });

  return recordTypes;
}
